import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { logAdminActivity } from "@/services/adminActivityService";

/**
 * White-label branding belonging to a network admin. Resolved server-side by
 * SECURITY DEFINER RPCs so a caller can only ever see branding that applies to
 * them (their own network, the network of a show they are viewing, or the
 * network named in an invite link).
 */
export interface NetworkBranding {
  name: string | null;
  logo_url_light: string | null;
  logo_url_dark: string | null;
  hide_powered_by: boolean;
  hide_marketing_menu?: boolean;
  dashboard_logo_height?: number;
  dashboard_logo_padding?: number;
}

/** PODGO-admin-only white-label flags (the DB trigger rejects anyone else). */
export interface NetworkBrandingPatch {
  is_enabled?: boolean;
  hide_powered_by?: boolean;
  hide_marketing_menu?: boolean;
  branded_emails_enabled?: boolean;
  branded_emails_auth?: boolean;
  branded_emails_notices?: boolean;
  branded_emails_invites?: boolean;
  branded_emails_events?: boolean;
  custom_domain?: string | null;
  custom_domain_status?: "pending" | "active";
  /** Email look; network admins may also write this for their own network. */
  email_style?: Record<string, unknown>;
  dashboard_logo_height?: number;
  dashboard_logo_padding?: number;
}

/** Single write path for PODGO admins changing a network's white-label settings. */
export function useUpdateNetworkBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, patch }: { userId: string; patch: NetworkBrandingPatch }) => {
      const { error } = await (supabase as any)
        .from("network_branding")
        .upsert({ network_admin_id: userId, ...patch }, { onConflict: "network_admin_id" });
      if (error) throw error;
    },
    onSuccess: (_d, { userId, patch }) => {
      qc.invalidateQueries({ queryKey: ["network-branding-admin"] });
      qc.invalidateQueries({ queryKey: ["network-branding"] });
      toast.success("White label updated");
      void logAdminActivity("network_white_label_updated", "Updated network white-label settings", {
        user_id: userId,
        ...patch,
      });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** RPCs are newer than the generated types; the shape is asserted here. */
async function callBrandingRpc(
  fn: string,
  args?: Record<string, unknown>,
): Promise<NetworkBranding | null> {
  const { data, error } = await (supabase as any).rpc(fn, args ?? {});
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as NetworkBranding | undefined) ?? null;
}

const BRANDING_QUERY_OPTIONS = {
  staleTime: 5 * 60_000,
  gcTime: 30 * 60_000,
  retry: false,
} as const;

/** Branding for the signed-in user: their own network, or the one they belong to. */
export function useActiveBranding(enabled = true) {
  return useQuery({
    queryKey: ["network-branding", "active"],
    queryFn: () => callBrandingRpc("get_active_branding"),
    enabled,
    ...BRANDING_QUERY_OPTIONS,
  });
}

/** Branding for a public show page, resolved through network show assignments. */
export function useShowBranding(showId?: string | null) {
  return useQuery({
    queryKey: ["network-branding", "show", showId],
    queryFn: () => callBrandingRpc("get_show_branding", { _show_id: showId }),
    enabled: !!showId,
    ...BRANDING_QUERY_OPTIONS,
  });
}

/** Branding for the sign-in screen, reached via `/n/<slug>` (or `/auth?n=<slug>`). */
export function useBrandingBySlug(slug?: string | null) {
  return useQuery({
    queryKey: ["network-branding", "slug", slug],
    queryFn: () => callBrandingRpc("get_branding_by_slug", { _slug: slug }),
    enabled: !!slug,
    ...BRANDING_QUERY_OPTIONS,
  });
}

/** Branding for the sign-in screen reached via a network member's referral link (`?ref=`). */
export function useBrandingByReferral(code?: string | null) {
  return useQuery({
    queryKey: ["network-branding", "referral", code],
    queryFn: () => callBrandingRpc("get_branding_by_referral", { _code: code }),
    enabled: !!code,
    ...BRANDING_QUERY_OPTIONS,
  });
}

/** Hostnames that are always PODGO's own; never worth a branding lookup. */
const PODGO_HOSTS = new Set(["localhost", "127.0.0.1", "podgo.io", "www.podgo.io", "links.podgo.io"]);

// Skipping the lookup on PODGO's own domains matters: the homepage waits for
// it before choosing between the marketing page and a network's sign-in.
function brandableHost(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  // Editor previews run on *.lovableproject.com / *.lovable.app; treat them as PODGO's own.
  if (
    PODGO_HOSTS.has(host) ||
    host.endsWith(".lovable.app") ||
    host.endsWith(".lovableproject.com") ||
    host.endsWith(".lovable.dev")
  ) return null;
  return host;
}

/** True on any domain that isn't one of PODGO's own (a network's custom domain). */
export function isAlternateHost(): boolean {
  return brandableHost() !== null;
}

/** Branding for a network's own custom domain, resolved from the current hostname. */
export function useBrandingByHost() {
  const host = brandableHost();
  return useQuery({
    queryKey: ["network-branding", "host", host],
    queryFn: () => callBrandingRpc("get_branding_by_host", { _host: host }),
    enabled: !!host,
    ...BRANDING_QUERY_OPTIONS,
  });
}

/** Whether PODGO turned branded emails on for the signed-in network admin's network. */
export function useOwnBrandedEmailsEnabled(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["network-branding", "branded-emails", user?.id],
    enabled: enabled && !!user?.id,
    queryFn: async (): Promise<boolean> => {
      const { data } = await (supabase as any)
        .from("network_branding")
        .select("is_enabled, branded_emails_enabled")
        .eq("network_admin_id", user!.id)
        .maybeSingle();
      return !!(data?.is_enabled && data?.branded_emails_enabled);
    },
  });
}
